import { MustClickLink } from "../../components/must-click-link"
import { useAppState } from "../../hooks"
import * as Sentry from "@sentry/react"
import api from '../../api-client'
import { SubformProps } from "."
import { Spinner } from '@fluentui/react'
import { useState } from "react"
import { Audience, ESIGN_AGREEMENT_URL, PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL, TEST_APPLICATION_PREFILL, TEST_APPLICATION_PREFILL_COBORROWER, errorToString, getBrowserFingerprintData } from "@oneethos/shared"
import { AgreementType } from "@oneethos/shared"
import { toast } from "react-toastify"
import AgreementCheckbox from "../../components/agreement-checkbox"
import { Modal } from "../../components/modal"

type ReviewTermsProps = Partial<SubformProps> & {
  enableSave: boolean
  onAfterSave?: (...any) => void
}

const PdfModal = ({ show, onClose, title, url }) => {
  const [loading, setLoading] = useState(false)

  const handleDownload = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      window.open(url, '_blank')
    } catch (error) {
      toast.error(errorToString(error))
    } finally {
      setLoading(false)
    }
  }

  if (!show) return null

  return (
    <Modal onClose={onClose}>
      <h4 className="mb-3">{title}</h4>
      <object data={url} type="application/pdf" width="100%" height="600px" className="mb-3">
        <p>
          Unable to display {title} PDF file. 
          {loading ? (
            <span>Loading...</span>
          ) : (
            <a href="#" onClick={handleDownload}> Download</a>
          )} instead.
        </p>
      </object>
    </Modal>
  )
}

export const ReviewTerms = ({
  formData,
  update,
  saving,
  enableSave,
  onAfterSave
}: ReviewTermsProps) => {
  const { tenant: { config } } = useAppState()

  // the UI prevents them from accepting the terms without clicking the links,
  // so we can safely assume that if they're returning to this page and previously 
  // agreed to terms, they've clicked the links
  const agrees = formData.agreesToTermsAndConditions?.agrees

  const [hasClickedTerms, setHasClickedTerms] = useState(agrees)
  const [hasClickedPrivacy, setHasClickedPrivacy] = useState(agrees)
  const [hasClickedEsign, setHasClickedEsign] = useState(agrees)

  const hasClickedAll = hasClickedTerms && hasClickedPrivacy && hasClickedEsign

  const [savingAgreements, setSavingAgreements] = useState(false)

  const [showESignModal, setshowESignModal] = useState(false)
  const [showTerms, setShowTerms] = useState(false)
  const [showPrivacy, setShowPrivacy] = useState(false)

  const {
    termsAndConditionsUrl = TERMS_AND_CONDITIONS_URL,
    privacyPolicyUrl = PRIVACY_POLICY_URL,
    esignAgreementUrl = ESIGN_AGREEMENT_URL
  } = config

  const saveAgreements = async () => {
    setSavingAgreements(true)
    const browser = getBrowserFingerprintData()

    try {
      // ensure all consents are recorded before proceeding
      await Promise.all([
        { url: esignAgreementUrl, type: AgreementType.EsignAgreement },
        { url: privacyPolicyUrl, type: AgreementType.PrivacyPolicy },
        { url: termsAndConditionsUrl, type: AgreementType.ConsumerTerms }
      ].map(agreement => api.post('/consents/accept', {
        browser,
        agrees: true,
        agreementUrl: agreement.url,
        agreementType: agreement.type,
        audience: Audience.Consumer,
        loanApplicationId: formData._id
      })))
      setSavingAgreements(false)
    } catch (ex) {
      setSavingAgreements(false)
      toast.error('Error recording agreements, please try again. If the problem persists, please contact support.')
      Sentry.captureException(ex, {
        tags: {
          error: 'Error recording agreements'
        }
      })
    }
  }

  return (
    <div className="review-terms">
      <PdfModal
        show={showESignModal}
        onClose={() => setshowESignModal(false)}
        title="eSign Agreement"
        url={esignAgreementUrl}
      />
      <PdfModal
        show={showPrivacy}
        onClose={() => setShowPrivacy(false)}
        title="Privacy Policy"
        url={privacyPolicyUrl}
      />
      <PdfModal
        show={showTerms}
        onClose={() => setShowTerms(false)}
        title="Terms and Conditions"
        url={termsAndConditionsUrl}
      />
      <span className="me-2">Please review our</span>
      <MustClickLink
        clicked={hasClickedEsign}
        label="eSign Agreement"
        onClicked={() => {
          setshowESignModal(true)
          setHasClickedEsign(true)
        }}
      />,
      &nbsp;
      <MustClickLink
        clicked={hasClickedPrivacy}
        label="privacy policy"
        onClicked={() => {
          setShowPrivacy(true)
          setHasClickedPrivacy(true)
        }}
      />,
      &nbsp;and&nbsp;
      <MustClickLink
        clicked={hasClickedTerms}
        label="terms and conditions"
        onClicked={() => {
          setShowTerms(true)
          setHasClickedTerms(true)
        }}
      />
      <p className="mt-3 fw-bold">IMPORTANT INFORMATION ABOUT PROCEDURES FOR OPENING A NEW ACCOUNT</p>
      <p>
        To help the government fight the funding of terrorism and money laundering
        activities, Federal law requires all financial institutions to obtain, verify
        and record information that on personal accounts identifies each person who
        opens an account. In addition, on legal entity accounts, we will require
        identification on beneficial owners and controlling person. What this means
        for you: When you open an account, we will ask for your name, address, date of
        birth, and other information that will allow us to identify you. We may also
        ask to see your driver's license or other identifying documents.
      </p>
      {hasClickedAll ? <AgreementCheckbox
        formData={formData}
        update={update}
        name="agreesToTermsAndConditions"
      >
        I have read and agree with eSign Agreement,
        privacy policy, and terms and conditions.
      </AgreementCheckbox> : null}

      {(enableSave && hasClickedAll) ? <div className="buttons d-flex flex-column flex-md-row gap-2 justify-content-center">
        <button type="button"
          className="btn btn-primary"
          disabled={saving}
          onClick={async () => {
            await saveAgreements()
            onAfterSave?.()
          }}
        >{saving || savingAgreements ? <Spinner /> : 'Continue'}</button>
        {process.env.REACT_APP_NODE_ENV === 'test' ? <>
          <button type="button"
            className="btn btn-secondary"
            disabled={saving}
            onClick={async () => {
              await saveAgreements()
              onAfterSave?.({
                advance: false,
                data: {
                  ...formData,
                  preSubmitStatus: 'Eligibility',
                  ...TEST_APPLICATION_PREFILL
                }
              })
            }}
          >Prefill single applicant</button>
          <button type="button"
            className="btn btn-secondary"
            disabled={saving}
            onClick={async () => {
              await saveAgreements()
              onAfterSave?.({
                advance: false,
                data: {
                  ...formData,
                  preSubmitStatus: 'Eligibility',
                  ...TEST_APPLICATION_PREFILL,
                  ...TEST_APPLICATION_PREFILL_COBORROWER
                }
              })
            }}
          >Prefill co-applicant</button>
        </> : null}
      </div> : null}
    </div>)
}